import type { CSSProperties } from 'vue'
import type { IIConProps } from '../types'
import { cssPropsToString } from '@/utils/style'
import { elementUpdateAttribute, parseHTML } from '@/utils/html'
import { isFunction } from 'math'

type NativeElement = HTMLElement | SVGElement

const iconModules = import.meta.glob('assets/img/svg/**/*.svg', {
  query: '?raw',
  eager: true,
  import: 'default'
})

export const getRawFromModule = (content: string): null | string => {
  let path = '/src' + content.split('@')[1]
  if (path.includes('?')) {
    path = path.split('?')[0]
  }
  if (iconModules && iconModules[path]) return iconModules[path] as string
  return null
}

export const getSVGTree = (content: string) => {
  try {
    const tree = parseHTML<SVGElement>(content)
    if (!tree) return null
    if (tree.tagName !== 'svg') return null
    return tree
  } catch (e) {
    console.error(e);
    return null;
  }
}

const cache: Record<string, string> = {}

export const fetchContent = async (url: string) => {
  if (!url) return null
  if (cache[url]) return cache[url]

  try {
    const resp = await fetch(url)
    const source = await resp.text()
    if (!source) return null
    cache[url] = source
    return source
  } catch (e) {
    console.error(e)
  }
}

export const fixSVGPaths = (el: SVGElement, props: IIConProps) => {
  if (!isFunction(el.getAttribute)) return
  if (!isFunction(el.setAttribute)) return
  elementUpdateAttribute(el as unknown as HTMLElement, 'stroke', props.stroke || 'none')
  if (el.getAttribute('width') !== el.getAttribute('height')) return
  if (props.size) {
    el.setAttribute('width', props.size + '')
    el.setAttribute('height', props.size + '')
  }
  if (
    !el.querySelector('g') ||
    !el.querySelector('defs') ||
    (el as unknown as HTMLElement).querySelectorAll('path').length < 2
  )
    return
  const svgPath = el.querySelector('path')
  if (svgPath && svgPath.parentElement && svgPath.parentElement.parentElement) {
    const upper = svgPath.parentElement?.parentElement
    upper.innerHTML = ''
    upper.appendChild(svgPath)
  }
}

export const modifyTree = (tree: SVGElement, props: IIConProps, style: CSSProperties) => {
  //const html = tree.outerHTML || tree.innerHTML;

  const stroke = props.stroke
  const fill = props.fill
  const size = props.size;
  const color = props.color

  const patchAttribute = (el: NativeElement, key: string, value: string) => {
    value = value + '';
    if ((el.hasAttribute(key) && el.getAttribute(key) !== 'none') || value.includes('-')) {
      el.setAttribute(key, value)
    }
  }


  if (size) {
    tree.setAttribute('width', size + '')
    tree.setAttribute('height', size + '')
  }

  if (props.width) {
    tree.setAttribute('width', props.width + '')
  }

  if (props.height) {
    tree.setAttribute('height', props.height + '')
  }

  if (props.viewBox) {
    tree.setAttribute('viewBox', props.viewBox)
  }

  tree.setAttribute(
    'style',
    cssPropsToString({
      ...(stroke && stroke !== 'original'
        ? {
            stroke: stroke
          }
        : {}),
      ...(fill && fill !== 'original'
        ? {
            fill: fill
          }
        : {}),
      ...style
    } satisfies CSSProperties)
  )

  if (color) {
    patchAttribute(tree, 'color', color)
  }

  if (stroke && stroke !== 'original') {
    patchAttribute(tree, 'stroke', stroke)
  }

  if (fill && fill !== 'original') {
    patchAttribute(tree, 'fill', fill)
  }

  tree.querySelectorAll('rect').forEach((el) => {
    if (stroke && stroke !== 'original') {
      patchAttribute(el, 'stroke', stroke)
    }
    if (fill && fill !== 'original') {
      patchAttribute(el, 'fill', fill)
    }
  })

  tree.querySelectorAll('path').forEach((el, i) => {
    const pathStroke = (props.pathOptions ? props.pathOptions[i]?.stroke : undefined) || stroke;
    const pathFill = (props.pathOptions ? props.pathOptions[i]?.fill : undefined) || fill;
    if (pathStroke && pathStroke  !== 'original') {
      patchAttribute(el, 'stroke', pathStroke)
    }
    if (pathFill && pathFill !== 'original') {
      patchAttribute(el, 'fill', pathFill)
    }
  })
}
